/* WP-10 date helpers: HKT business timezone, locale weekday names (§14.7). */

const HKT = 'Asia/Hong_Kong';

const localeTag = (locale: string) =>
  locale === 'zh-hant' ? 'zh-HK' : locale === 'zh-hans' ? 'zh-CN' : 'en-GB';

export function formatDate(
  iso: string | Date,
  locale: string,
  opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' },
): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return new Intl.DateTimeFormat(localeTag(locale), { timeZone: HKT, ...opts }).format(d);
}

export function formatDateTime(iso: string | Date, locale: string): string {
  return formatDate(iso, locale, {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatWeekday(iso: string | Date, locale: string): string {
  return formatDate(iso, locale, { weekday: 'long' });
}

export function formatDateOverline(iso: string | Date, locale: string): string {
  return formatDate(iso, locale, { weekday: 'long', day: 'numeric', month: 'long' });
}

export function formatMonthKey(monthKey: string, locale: string): string {
  const [y, m] = monthKey.split('-').map(Number);
  const d = new Date(Date.UTC(y, (m ?? 1) - 1, 15));
  return new Intl.DateTimeFormat(localeTag(locale), {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

export function dayNumber(iso: string | Date): number {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return Number(
    new Intl.DateTimeFormat('en-GB', { timeZone: HKT, day: '2-digit' }).format(d),
  );
}

export function shortDow(iso: string | Date, locale: string): string {
  return formatDate(iso, locale, { weekday: 'short' });
}

/** YYYY-MM-DD in HKT — used for preferred pickup dates + month pickers. */
export function hktDateKey(d: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: HKT,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/** Weekday index (0=Sun..6=Sat) in HKT for closed-weekday checks. */
export function hktWeekday(d: Date): number {
  const name = new Intl.DateTimeFormat('en-US', { timeZone: HKT, weekday: 'short' }).format(d);
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(name);
}

/* ---------------------------------------------------------------------------
 * F2 — civil-date helpers for server-owned YYYY-MM-DD keys (§11.3).
 * A date key is a business calendar date, not an instant: `new Date(key)`
 * parses as UTC midnight and then displays as the PREVIOUS day in any
 * negative-offset timezone. These helpers anchor labels and weekdays to the
 * key itself (formatted under timeZone 'UTC'), so a chip always shows the
 * date the server returned no matter where the browser sits.
 * ------------------------------------------------------------------------- */

const YMD_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** UTC instant standing for the civil date key, or null when malformed. */
function civilUtc(key: string): Date | null {
  const m = YMD_KEY_RE.exec(key);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const t = new Date(Date.UTC(y, mo - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === mo - 1 && t.getUTCDate() === d
    ? t
    : null;
}

/** Civil weekday (0=Sun..6=Sat) of a date key — timezone-free. */
export function civilWeekday(key: string): number | null {
  return civilUtc(key)?.getUTCDay() ?? null;
}

/** Day-of-month number of a date key. */
export function civilDayNumber(key: string): number {
  return civilUtc(key)?.getUTCDate() ?? 0;
}

/** Locale label for a civil date key (weekday/day/month), never day-shifted. */
export function formatCivilDate(
  key: string,
  locale: string,
  opts: Intl.DateTimeFormatOptions,
): string {
  const d = civilUtc(key);
  if (!d) return '';
  return new Intl.DateTimeFormat(localeTag(locale), { timeZone: 'UTC', ...opts }).format(d);
}

export function civilDowLabel(key: string, locale: string): string {
  return formatCivilDate(key, locale, { weekday: 'short' });
}

/**
 * Inclusive list of civil date keys between min and max (both YYYY-MM-DD),
 * capped at `cap` entries — response sizes stay bounded even if a contract
 * ever returns an absurd range. Empty when the range is inverted.
 */
export function civilDatesBetween(minKey: string, maxKey: string, cap = 370): string[] {
  if (!civilUtc(minKey) || !civilUtc(maxKey) || minKey > maxKey) return [];
  const start = civilUtc(minKey)!.getTime();
  const out: string[] = [];
  for (let i = 0; i < cap; i += 1) {
    const key = new Date(start + i * 86_400_000).toISOString().slice(0, 10);
    if (key > maxKey) break;
    out.push(key);
  }
  return out;
}
